<template>  
  <div class="swiper-container">
    <swiper :options="heroBannerMobileOptions" ref="heroCarouselDesktop">
      <swiper-slide v-for="(slide, index) in slides" :key="index">
        <div class="hero-banner" :class="[layoutHorizontal, textAlign, heroSchema.mobileLayout]">
          <div class="hero-banner__container" :class="[layoutVertical, {[slide.mobileBgColor]: heroSchema.mobileLayout === 'below'}]">
            <div class="hero-banner__image-mobile" :style="`margin-top: 54px; backgroundImage: url(${slide.imageMobile})`"></div>
            <div class="hero-banner__overlay" v-if="slide.overlayEnable" :class="slide.overlayColor" :style="{ opacity: slide.overlayOpacity }"></div>
            <div class="hero-banner__content" :class="[contentProperties.classes]" :style="{...contentProperties.style,  ...slideContainerStyle}">
              <h4 class="preheader" :style="styleColors(slide,'preHeadingColorMobile')"  :class="slide.preHeadingColorMobile" v-html="slide.preHeading"></h4>
              <h2 class="h1 hero-banner__heading" :style="styleColors(slide,'headingColorMobile')" :class="slide.headingColorMobile" v-html="slide.heading"></h2>
              <h3 class="subheader" :style="styleColors(slide,'subHeadingColorMobile')" v-if="slide.subHeading" :class="slide.subHeadingColorMobile" v-html="slide.subHeading"></h3>
              <p class="body-md hero-banner__body-copy" :style="styleColors(slide,'bodyCopyColorMobile')" :class="slide.bodyCopyColorMobile" v-html="slide.copy"></p>
              <div class="hero-banner__cta-wrapper" v-if="slide.ctaText">
                <a v-if="slide.ctaLayout.includes('button')" :href="slide.ctaUrl" :class="slide.ctaLayout">
                <span class="button__content">{{slide.ctaText}}</span>
               </a>
                <v-link class="hero-banner__cta" v-if="slide.ctaLayout.includes('link')" :href="slide.ctaUrl" :class="slide.ctaLayout">{{slide.ctaText}}</v-link>
              </div>
            </div>
          </div>
        </div>
      </swiper-slide>
    </swiper>
    <div class="controls-container" :class="[heroSchema.mobileLayout, { 'controls-container--text-box': heroSchema.enableTextBox }]">
      <div class="pagination-dots">
        <div class="dot" v-for="(slide, index) in slides" :key="`dot-${index}`" @click="slidePagination(index)" :class="[dotActive(index), `dot-${index}`]"></div>
      </div>
    </div>
  </div>
  
</template>

<script>
import { flexSwitch } from '@/scripts/helpers/flex.js'
import { textAlignSwitch } from '@/scripts/helpers/textAlign.js'
import { scrollBelowElement } from '@/scripts/helpers/scroll.js'
import Icon from '@/scripts/components/basic/Icon.vue'
import VButton from '@/scripts/components/buttons/VButton.vue'
import VLink from '@/scripts/components/buttons/VLink.vue'
import css from 'swiper/swiper-bundle.min.css'
import { swiper, swiperSlide } from 'vue-awesome-swiper'
import { px } from '@/scripts/filters/string'

export default {
  name: 'HeroBannerCarouselMobile',
  components: {
    swiper,
    swiperSlide,
    Icon,
    VButton,
    VLink
  },
  props: {
    heroSchema: {
      type: Object,
      default: () => ({
        topPadding: Number,
        bottomPadding: Number,
        layoutHorizontal: String,
        layoutHorizontalTextBox: String,
        layoutVertical: String,
        mobileLayout: String,
        mobileTextAlignment: String,
        arrowColor: String,
        enableTextBox: Boolean,
        textBoxBgColor: String,
        textBoxBgImage: String
      })
    },
    heroArray: {
      type: Array,
      default: () => [
        {
          bodyCopyColorMobile: String,
          copy: String,
          copyColor: String,
          ctaLayout: String,
          ctaText: String,
          ctaUrl: String,
          enableCta: Boolean,
          headingColorMobile: String,
          image: String,
          imageMobile: String,
          imageMobileEnable: false,
          mobileBgColor: String,
          overlayColor: String,
          overlayEnable: true,
          overlayOpacity: 80,
          preHeading: String,
          preHeadingColor: String,
          preHeadingColorMobile: String,
          subHeading: String,
          subHeadingColor: String,
          subHeadingColorMobile: String
        }
      ]
    }
  },
  data () {
    return {
      heroBannerMobileOptions: {
        slidesPerView: 1,
        loop: true,
        threshold: 5,
        autoplay: {
         delay: parseInt(this.heroSchema.slideDuration),
        }
      },
      currentIndex: 0,
      sliderIndex: []
    }
  },
  computed: {
    layoutHorizontal () {
      return flexSwitch('justify', this.heroSchema.layoutHorizontal)
    },
    layoutVertical () {
      if (this.heroSchema.enableTextBox) {
        return flexSwitch('justify', 'center')
      }
      return flexSwitch('justify', this.heroSchema.layoutVertical)
    },
    textAlign () {
      return textAlignSwitch(this.heroSchema.mobileTextAlignment)
    },
    slides () {
       const sliders= this.heroArray.map((slide, index) => {
        return {
          ...slide,
          imageMobile: slide.imageMobileEnable
            ? slide.imageMobile
            : slide.image,
          overlayOpacity: `${slide.overlayOpacity / 100}`
        }
      })
      if (this.heroSchema.playMode === "random") {
        sliders.sort(() => Math.random() - 0.5);
      }
      this.sliderIndex=sliders;
      return sliders;
    },
    swiper () {
      return this.$refs.heroCarouselDesktop.swiper
    },
    contentProperties () {
      return {
        classes: this.heroSchema.enableTextBox
          ? [
            {
              'hero-banner__content--text-box': true,
              'hero-banner__content--text-box-img': this.heroSchema
                .textBoxBgImage
            },
            this.heroSchema.textBoxBgColor
          ]
          : undefined,
        style:
          this.heroSchema.enableTextBox && this.heroSchema.textBoxBgImage
            ? { backgroundImage: `url(${this.heroSchema.textBoxBgImage})` }
            : undefined
      }
    },
    slideContainerStyle () {
      const {
        heroSchema: { topPadding, bottomPadding }
      } = this

      return {
        paddingTop: px(36),
        paddingBottom: px(bottomPadding),
        background : this.slides[this.currentIndex].mobileBgColor
      }
    }
  },
  methods: {
    slidePrev () {
      this.swiper.slidePrev()
    },
    slideNext () {
      this.swiper.slideNext()
    },
    slidePagination (index) {
      this.swiper.slideToLoop(index)
    },
    styleColors(slide, key) {
       if (key === 'headingColorMobile') {
        return {
          color: slide.headingColorMobile
        }
       }else if (key === 'subHeadingColorMobile') {
        return {
          color: slide.subHeadingColorMobile
        }
       } else if (key === 'preHeadingColorMobile') {
        return {
          color: slide.preHeadingColorMobile
        }
       } else if (key === 'bodyCopyColorMobile') {
        return {
          color: slide.bodyCopyColorMobile
        }
       } 
    },
    dotActive (index) {
      if (this.currentIndex === index) {
        return 'active'
      }
    }
  },
  mounted () {
    this.$nextTick(function () {
        // Code that will run only after the
        // entire view has been rendered
          const queryParams = new URLSearchParams(window.location.search);
          let slideIndex = 1;
          const tag = queryParams.get('slide');
          slideIndex= this.sliderIndex.findIndex(element => element.slideTag==tag)
          if (slideIndex !==null && slideIndex !=='' && slideIndex !==-1 ) { 
            this.$refs.heroCarouselDesktop.swiper.slideTo(slideIndex+1);
          }

          if (this.heroSchema.autoPlay=="false") { 
            this.$refs.heroCarouselDesktop.swiper.autoplay.stop();
          } else {
            this.$refs.heroCarouselDesktop.swiper.autoplay.start()
          }


    })

    this.$watch(
      () => {
        return this.$refs.heroCarouselDesktop.swiper.realIndex
      },
      val => {
        this.currentIndex = val
      }
    )
  }
}
</script>

<style scoped lang="scss">
  .swiper-container {
    background: #F9F7F0;
  }
  .hero-banner {
    position: relative;
    display: flex;
    width: 100%;
    height: 672px;
    padding: 64px;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    text-decoration: none;
  
    &__overlay {
      display: none;
    }

    &__heading {
      margin-bottom: rem(16);
      line-height: (48 / 42);
    }

    &.overlay {
      
      min-height: 630px;

      .hero-banner {
        &__image-mobile {
          width: 100%;
        }

        &__container {
          flex: 1;
          display: inline-flex;
          flex-direction: column;
          text-decoration: none;
        }

        &__overlay {
          display: block;
          position: absolute;
          width: 100%;
          height: 100%;
        }
      }
    }
      
    &.below {
      min-height: 100vh;
      justify-content: flex-start;

      .hero-banner__content--text-box {
        position: relative;
        top: -30px;
      }
    }

    @include mobile-only {
      position: auto;
      height: auto;
      flex-direction: column;
      padding: 0;
    }
    
    &__container {
      flex: 1;
    }

    &__content {
      display: inline-flex;
      flex-direction: column;
      max-width: 100%;
      width: 100%;
      z-index: 21;

      &--text-box {
        margin: 0 16px;
        width: calc(100% - 32px);
        border-radius: $hero-content-border-radius;
      }

      @include desktop-up {
        max-width: 672px;
      }
      @include mobile-only {
        padding: 0 14px;
      }
    }
    
    &__body-copy {
      margin: 16px 0;

      @include desktop-up {
        margin: 24px 0;
      }
    }
      
    &__cta-wrapper {
      display: inline-block;
    }

    &__overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }

    &__scroll-trigger {
      width: 58px;
      height: 58px;
      padding: 20px;
      position: absolute;
      left: 50%;
      right: 50%;
      bottom: 8px;
      transform: translate(-50%, 0);
      cursor: pointer;
    }
      
    &__image-mobile {
      position: relative;
      height: 344px;
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
    }
  }

  .controls-container {
    display: flex;
    position: absolute;
    bottom: 24px;
    width: 100%;
    z-index: 5;

    @include mobile-only {
      justify-content: center;
    }

    .pagination-dots {
      display: flex;
      align-items: center;
      cursor: pointer;
    }

    .dot {
      width: 6px;
      height: 6px;
      border-radius: 100%;
      background-color: $color-grey;
      margin: 4px;
    }

    .dot.active {
      background-color: $color-off-black;
    }
  }
</style>
